/**
 * 활동 로그 필터 컴포넌트
 * activity-logs/admin/index.tsx에서 분리
 */
import React from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { CustomerAutocomplete } from '@/components/ui/CustomerAutocomplete';
import { Search, X } from 'lucide-react';
import { Customer } from '@/types';
import { ActivityLogFilters as FilterType } from '@/hooks/common';

interface ActivityType {
  value: string;
  label: string;
}

interface ActivityLogFiltersProps {
  filters: FilterType;
  onFilterChange: (filters: FilterType) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
  activityTypes: ActivityType[];
  showCustomerFilter?: boolean;
  showCategoryFilter?: boolean;
  showLogLevelFilter?: boolean;
  customers?: Customer[];
  availableCategories?: { value: string; label: string }[];
}

export const ActivityLogFilters: React.FC<ActivityLogFiltersProps> = ({
  filters,
  onFilterChange,
  onSearch,
  onReset,
  loading = false,
  activityTypes,
  showCustomerFilter = false,
  showCategoryFilter = false,
  showLogLevelFilter = false,
  customers = [],
  availableCategories = [],
}) => {
  const handleFilterUpdate = (key: keyof FilterType, value: string | number | undefined) => {
    onFilterChange({ ...filters, [key]: value });
  };

  return (
    <div className="mb-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 시작 날짜 */}
        <div>
          <label className="text-sm font-medium mb-1 block">시작 날짜</label>
          <Input
            type="datetime-local"
            value={filters.startDate}
            onChange={(e) => handleFilterUpdate('startDate', e.target.value)}
          />
        </div>

        {/* 종료 날짜 */}
        <div>
          <label className="text-sm font-medium mb-1 block">종료 날짜</label>
          <Input
            type="datetime-local"
            value={filters.endDate}
            onChange={(e) => handleFilterUpdate('endDate', e.target.value)}
          />
        </div>

        {/* 고객사 필터 (슈퍼관리자만) */}
        {showCustomerFilter && (
          <CustomerAutocomplete
            customers={customers}
            value={filters.customerId}
            onChange={(customerId) => handleFilterUpdate('customerId', customerId)}
            label="고객사 ID"
            placeholder="고객사 코드 또는 이름 검색"
          />
        )}

        {/* 사용자 검색 */}
        <div>
          <label className="text-sm font-medium mb-1 block">사용자 검색</label>
          <Input
            type="text"
            value={filters.userSearch}
            onChange={(e) => handleFilterUpdate('userSearch', e.target.value)}
            placeholder="이메일 또는 이름"
          />
        </div>

        {/* 카테고리 필터 */}
        {showCategoryFilter && (
          <div>
            <label className="text-sm font-medium mb-1 block">카테고리</label>
            <Select
              value={filters.activityCategory}
              onValueChange={(value) => handleFilterUpdate('activityCategory', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="전체" />
              </SelectTrigger>
              <SelectContent>
                {availableCategories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* 활동 유형 */}
        <div>
          <label className="text-sm font-medium mb-1 block">활동 유형</label>
          <Select
            value={filters.activityType}
            onValueChange={(value) => handleFilterUpdate('activityType', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              {activityTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 설명 검색 */}
        <div>
          <label className="text-sm font-medium mb-1 block">설명 검색</label>
          <Input
            type="text"
            value={filters.descriptionSearch}
            onChange={(e) => handleFilterUpdate('descriptionSearch', e.target.value)}
            placeholder="설명 키워드"
          />
        </div>

        {/* 로그 레벨 필터 */}
        {showLogLevelFilter && (
          <div>
            <label className="text-sm font-medium mb-1 block">로그 레벨</label>
            <Select
              value={filters.logLevel}
              onValueChange={(value) => handleFilterUpdate('logLevel', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="전체" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem key="log-level-all" value="all">전체</SelectItem>
                <SelectItem key="log-level-INFO" value="INFO">INFO</SelectItem>
                <SelectItem key="log-level-WARN" value="WARN">WARN</SelectItem>
                <SelectItem key="log-level-ERROR" value="ERROR">ERROR</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* 액션 버튼 */}
      <div className="flex justify-end gap-2">
        <Button onClick={onReset} variant="outline">
          <X className="mr-2 h-4 w-4" />
          초기화
        </Button>
        <Button onClick={onSearch} disabled={loading}>
          <Search className="mr-2 h-4 w-4" />
          검색
        </Button>
      </div>
    </div>
  );
};

export default ActivityLogFilters;
